import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Customer } from '@/types/customer-type';
import { Fulfillment } from '@/types/fulfillment-type';
import { OrderResponse, PaymentRecord } from '@/types/order-type';
import { CreditCard, DollarSign, MapPin, Package, Phone, Truck } from 'lucide-react';
import { carrierLabel, formatPrice, isDeliveryMethod, methodLabel, SmallInfoTile } from './fulfillment-display';

interface PublicOrderInfoProps {
  customer: Customer;
  order: OrderResponse;
  fulfillment: Fulfillment | null;
  payments: PaymentRecord[];
}

export function PublicOrderAddress({ customer, order, fulfillment, payments }: PublicOrderInfoProps) {
  const hasAddress = Boolean(order.shippingAddress && order.shippingAddress.trim());

  return (
    <div className="space-y-4">
      <Card className="rounded-xl border border-slate-200 shadow-sm">
        <CardHeader className="px-4 py-3">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-slate-600" />
            <h3 className="text-sm font-semibold text-slate-800">Shipping Address</h3>
          </div>
        </CardHeader>

        <CardContent className="px-4 pb-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="truncate text-sm font-medium text-slate-900">{customer.name || 'Unknown'}</p>
              {customer.phone && (
                <span className="inline-flex shrink-0 items-center gap-1 text-xs text-slate-600">
                  <Phone className="h-3.5 w-3.5" />
                  {customer.phone}
                </span>
              )}
            </div>

            <div className="mt-2 border-t border-slate-200 pt-2">
              {hasAddress ? (
                <div className="whitespace-pre-line text-sm leading-relaxed text-slate-800">
                  {order.shippingAddress}
                </div>
              ) : (
                <p className="text-xs italic text-slate-500">No shipping address provided</p>
              )}
            </div>

            <div className="mt-3 border-t pt-2" />
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {fulfillment && (
                <SmallInfoTile
                  icon={<Package className="h-3.5 w-3.5 text-slate-500" />}
                  label="Shipping Method"
                  value={methodLabel(fulfillment.method)}
                />
              )}
              <SmallInfoTile
                icon={<CreditCard className="h-3.5 w-3.5 text-slate-500" />}
                label="Payment Method"
                value={payments.at(-1)?.method ?? 'Not specified'}
              />
              {fulfillment && isDeliveryMethod(fulfillment.method) && fulfillment.carrier && (
                <SmallInfoTile
                  icon={<Truck className="h-3.5 w-3.5 text-slate-500" />}
                  label="Carrier"
                  value={carrierLabel(fulfillment.carrier)}
                />
              )}
              <SmallInfoTile
                icon={<DollarSign className="h-3.5 w-3.5 text-slate-500" />}
                label="Delivery Fee"
                value={formatPrice(fulfillment?.price)}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
